// Frontend mirror of the backend IndexationUtilities: per-field indexation percentages applied to
// the previous year's value during "copy from last period". Field keys use the backend DTO property
// names (PascalCase); form field names derive from them via toFormFieldName, same as the value-fields
// visibility and change-threshold features.

import { toFormFieldName } from "./valueFieldsVisibility";

export const INDEXABLE_VALUE_FIELDS = [
  "BuildingValues",
  "EquipmentValues",
  "BusinessInteruptionValues12Months",
] as const;

export type IndexationFieldKey = (typeof INDEXABLE_VALUE_FIELDS)[number];

export interface IValueIndexationInfo {
  enabledFields: string[];
  percentages: Record<string, number>;
  isDefault: boolean;
  sourceClientId?: number | null;
  sourceLocationId?: number | null;
  sourceConfigurationSettingId?: number | null;
}

// Returns the effective indexation percentage for a form field (camelCase), or null when the field is
// not indexation-enabled / not configured. Only a positive configured percentage counts.
export const getEffectiveIndexationForField = (
  info: IValueIndexationInfo | null | undefined,
  formFieldName: string
): number | null => {
  if (!info || info.isDefault) return null;
  const fieldKey = INDEXABLE_VALUE_FIELDS.find(
    (k) => toFormFieldName(k) === formFieldName
  );
  if (!fieldKey) return null;
  const enabled = (info.enabledFields ?? []).map(toFormFieldName);
  if (!enabled.includes(formFieldName)) return null;
  // dictionary keys may arrive Pascal- or camelCased depending on the serializer
  const percent =
    info.percentages?.[fieldKey] ?? info.percentages?.[toFormFieldName(fieldKey)];
  return typeof percent === "number" && percent > 0 ? percent : null;
};

// Applies an indexation percentage to a previous value, rounded to 2 decimals to match form seeding.
export const applyIndexation = (value: number, percent: number): number =>
  Math.round(value * (1 + percent / 100) * 100) / 100;
